/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Section, Container } from '@tishman/components';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useMemo } from 'react';
import invariant from 'invariant';

import IntroText from '../components/IntroText';

import type { ComponentPropsWithoutRef } from 'react';

const ARCHIVED_BUSINESS_QUERY = graphql`
  query ArchivedBusinessHero {
    dataJson(jsonId: { eq: "archived-business" }) {
      archivedShopHero {
        title
        caption
      }
      archivedDiningHero {
        title
        caption
      }
    }
  }
`;

const ArchivedBusinessHeroBlock = (
  { data: { business } }: { data: Queries.BusinessDetailQuery },
  props: ComponentPropsWithoutRef<typeof Section>,
): React.JSX.Element => {
  const { dataJson } = useStaticQuery<Queries.ArchivedBusinessHeroQuery>(
    ARCHIVED_BUSINESS_QUERY,
  );

  invariant(dataJson, 'Shop and Dine Hero JSON data is required!');
  invariant(business, 'Business data is required!');

  const businessTitle =
    business?.category?.category === 'dine'
      ? `${business?.titleAndSlug?.title} ${dataJson.archivedDiningHero.title}`
      : `${business?.titleAndSlug?.title} ${dataJson.archivedShopHero.title}`;

  const businessCaption =
    business?.category?.category === 'dine'
      ? dataJson.archivedDiningHero.caption
      : dataJson.archivedShopHero.caption;

  const archivedBusinessData = useMemo(
    () => ({
      title: businessTitle,
      caption: businessCaption,
    }),
    [businessCaption, businessTitle],
  );

  return (
    <Section data-section="ArchiveBusinessHero" {...props}>
      <Container sx={{ py: 4, pb: [0, null, null, 4] }}>
        <IntroText
          caption={archivedBusinessData.caption}
          desktopOrientation="column"
          sx={{
            ...{
              'h1, h2, h3, h4, h5, h6': {
                fontSize: [6, null, 7],
              },
            },
            mb: 0,
            mt: [0, null, 6],
          }}
          title={archivedBusinessData.title}
        />
      </Container>
    </Section>
  );
};

export default ArchivedBusinessHeroBlock;
